import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import api from '../services/api';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    parent_id: '',
  });
  const [error, setError] = useState('');

  const mainCategories = categories.filter((c) => c.parent_id == null);

  const filteredCategories = categories.filter((category) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (category.name || '').toLowerCase().includes(term) ||
      (category.description || '').toLowerCase().includes(term) ||
      (category.parent_name || '').toLowerCase().includes(term)
    );
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || '',
        description: category.description || '',
        image_url: category.image_url || '',
        parent_id: category.parent_id != null ? String(category.parent_id) : '',
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        image_url: '',
        parent_id: '',
      });
    }
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '', image_url: '', parent_id: '' });
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Nome da categoria é obrigatório');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        image_url: formData.image_url.trim() || undefined,
        parent_id: formData.parent_id === '' ? null : Number(formData.parent_id),
      };
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, payload);
      } else {
        await api.post('/categories', payload);
      }
      handleCloseDialog();
      loadCategories();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      setError(error.response?.data?.error || 'Erro ao salvar categoria');
    }
  };

  const handleDelete = async (category) => {
    const hasChildren = (category.children_count || 0) > 0;
    const msg = hasChildren
      ? `Esta categoria possui ${category.children_count} subcategoria(s). Remova ou mova-as antes.`
      : category.products_count > 0
        ? `ATENÇÃO: Esta categoria possui ${category.products_count} produto(s). Não será possível deletá-la enquanto houver produtos usando esta categoria.`
        : 'Esta ação não pode ser desfeita.';
    if (window.confirm(`Tem certeza que deseja deletar a categoria "${category.name}"?\n\n${msg}`)) {
      try {
        await api.delete(`/categories/${category.id}`);
        loadCategories();
      } catch (error) {
        console.error('Erro ao deletar categoria:', error);
        alert(error.response?.data?.error || 'Erro ao deletar categoria');
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4, pt: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Gerenciar Categorias
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nova Categoria
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Categorias principais e subcategorias:</strong> Crie categorias principais (sem pai) ou subcategorias (vinculadas a uma principal).
          Você pode definir imagem (URL) e descrição. Ao editar o nome, os produtos que usam a categoria são atualizados.
          Não é possível deletar categoria que tenha subcategorias ou produtos vinculados.
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <TextField
          size="small"
          label="Pesquisar categorias"
          placeholder="Buscar por nome, descrição ou categoria pai"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 320 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Imagem</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Categoria pai</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="right">Produtos</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Nenhuma categoria cadastrada ainda. Clique em "Nova Categoria" para criar.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Nenhuma categoria encontrada para a pesquisa atual.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCategories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>
                    {category.image_url ? (
                      <Box
                        component="img"
                        src={category.image_url}
                        alt=""
                        sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', borderRadius: 1 }}>
                        <CategoryIcon color="action" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {category.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={category.parent_id == null ? 'Principal' : 'Subcategoria'}
                      size="small"
                      color={category.parent_id == null ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {category.parent_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }} noWrap>
                      {category.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={category.products_count || 0}
                      color={category.products_count > 0 ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(category)}
                      title="Editar categoria"
                    >
                      <EditIcon />
                    </IconButton>
                    <Tooltip
                      title={
                        (category.children_count || 0) > 0
                          ? 'Remova as subcategorias antes'
                          : category.products_count > 0
                            ? 'Não é possível deletar categoria com produtos'
                            : 'Deletar categoria'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(category)}
                          disabled={(category.children_count || 0) > 0 || (category.products_count || 0) > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {categories.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Resumo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total de categorias: {categories.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total de produtos: {categories.reduce((sum, cat) => sum + (cat.products_count || 0), 0)}
          </Typography>
        </Paper>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {editingCategory && editingCategory.products_count > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Esta categoria possui {editingCategory.products_count} produto(s). 
                Ao alterar o nome, todos os produtos serão atualizados automaticamente.
              </Typography>
            </Alert>
          )}
          <TextField
            fullWidth
            label="Nome da Categoria"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              setError('');
            }}
            margin="normal"
            required
            placeholder="Ex: Mobília Solta, Sofás e Poltronas..."
            helperText="Nome único da categoria"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Categoria pai</InputLabel>
            <Select
              value={formData.parent_id}
              label="Categoria pai"
              onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
            >
              <MenuItem value="">Nenhuma (categoria principal)</MenuItem>
              {mainCategories
                .filter((m) => !editingCategory || m.id !== editingCategory.id)
                .map((m) => (
                  <MenuItem key={m.id} value={String(m.id)}>
                    {m.name}
                  </MenuItem>
                ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Deixe em branco para criar uma categoria principal (grande). Selecione uma para criar subcategoria.
            </Typography>
          </FormControl>
          <TextField
            fullWidth
            label="URL da imagem"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            margin="normal"
            placeholder="https://..."
            helperText="Opcional. URL da imagem da categoria (principal ou subcategoria)."
          />
          <TextField
            fullWidth
            label="Descrição"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            placeholder="Descrição opcional da categoria..."
            helperText="Descrição opcional para ajudar na organização"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.name.trim()}
          >
            {editingCategory ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminCategories;
